/**
 * Helper component to create a Link to a specific role-protected page (like "admin")
 */

// import primary libraries
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, useLocation, useParams } from 'react-router-dom';

import apiUtils from '../../utils/api';

// import hooks
import { useLoggedInUser } from '../../../resources/user/authService';
import { useLoggedInGroupUser } from '../../../resources/groupUser/groupUserService';
import DefaultLayout from '../layouts/DefaultLayout';

const YTRoute = ({
  breadcrumbs
  , admin // user.admin
  , agreedToTerms// user.agreedToTerms
  , profile // user._profile
  // , accountManager // TBD, where is this supposed to come from?

  // groupUser based permissions
  , groupAdmin // groupUser.admin DEPRECATED SOON: This will be broken into more granular permissions like 'view-dashboard', 'manage-users', etc. For now still supporting this because it's all over the server and client code
  , viewReports // groupUser.permissions.includes('view-reports')
  , groupMember // groupUser._group === groupId // TODO: Implement this check, doesn't exist yet
  // planned but not used yet
  , authoring // groupUser.permissions.includes('authoring')
  , viewDashboard // groupUser.permissions.includes('view-dashboard')
  , viewUsers // groupUser.permissions.includes('view-users')
  , manageUsers // groupUser.permissions.includes('manage-users')
  , support // groupUser.permissions.includes('support')

  , login
  , exact
  , path
  , component
}) => {
  const location = useLocation();
  const { email } = apiUtils.objectFromQueryString(location.search);
  const { groupId } = useParams();

  /**
   * From current shared resources GroupUser schema:
   * 'view-dashboard' // user can access the rome dashboard - should be added if any other admin like permissions are added
      , 'view-users' // user can view group users in the group
      , 'manage-users' // user can add/remove group users
      , 'view-reports' // user can view group analytics
   */

  // define permission levels in an easy to read way
  const groupPermissionLevel = useMemo(() => {
    if(!groupId) return null; // no groupId, no permissions
    if(groupMember) return 'group-member';
    if(groupAdmin) return 'group-admin';
    if(viewReports) return 'view-reports';
    if(authoring) return 'authoring';
    if(viewDashboard) return 'view-dashboard';
    if(viewUsers) return 'view-users';
    if(manageUsers) return 'manage-users';
    if(support) return 'support';
    return null;
  }, [groupAdmin, authoring, viewReports, viewDashboard, viewUsers, manageUsers]);

  // use the hook to get the loggedInUser from the authStore
  const loggedInUser = useLoggedInUser();

  const skipGroupUserFetch = !groupPermissionLevel; // skip if no group permissions are needed
  const currentGroupUser = useLoggedInGroupUser(groupId, skipGroupUserFetch);
  const newLocation = { ...location };
  if(!newLocation.state) {
    newLocation.state = {}
  }
  newLocation.state.breadcrumbs = breadcrumbs;

  const hasPermission = useCallback((permission) => {
    // global admins always have permission
    if(loggedInUser?.admin) return true;
    // user based permissions
    if(!loggedInUser) return false;
    if(permission === 'admin') return loggedInUser.admin;
    if(permission === 'agreedToTerms') return loggedInUser.agreedToTerms;
    if(permission === 'profile') return loggedInUser._profile;
    // groupUser based permissions
    if(!currentGroupUser) return false;
    if(permission === 'group-member') return currentGroupUser._group === groupId;
    // catch for legacy groupAdmin permission
    if(permission === 'group-admin') return currentGroupUser.admin;
    return currentGroupUser.permissions?.includes(permission);
  }, [loggedInUser, currentGroupUser]);


  if(groupPermissionLevel && currentGroupUser === 'loading') {
    return <DefaultLayout.Skeleton />
  }

  // catch for loggedIn in all cases
  if((
    login
    || admin
    || agreedToTerms
    || profile
    || groupPermissionLevel
  ) && !loggedInUser
  ) {
    // if email is present, pass it along to the login page so we can pre-fill the username field
    const redirect = encodeURIComponent(window.location.href);
    return <Redirect to={`/user/login?${email ? `email=${encodeURIComponent(email)}&` : ''}redirect=${redirect}`} />
  }
  // User based permissions
  if(admin && !hasPermission('admin')) return <Redirect to={{ pathname: "/unauthorized" }} />
  if(agreedToTerms && !hasPermission('agreedToTerms')) return <Redirect to={{ pathname: "/unauthorized" }} />
  if(profile && !hasPermission('profile')) return <Redirect to={{ pathname: "/unauthorized" }} />
  // GroupUser based permissions
  if(groupPermissionLevel && !hasPermission(groupPermissionLevel)) {
    return <Redirect to={{ pathname: "/unauthorized" }} />
  }

  // no failed permissions, so render the component
  return (
    <Route
      exact={exact}
      path={path}
      component={component}
      location={newLocation}
    />
  )
}

YTRoute.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string
      , path: PropTypes.string
    })
  )
  , exact: PropTypes.bool
  , login: PropTypes.bool
  , path: PropTypes.string
  , admin: PropTypes.bool
  , agreedToTerms: PropTypes.bool
  , authoring: PropTypes.bool
  // , accountManager: PropTypes.bool
  , support: PropTypes.bool
}

YTRoute.defaultProps = {
  breadcrumbs: []
  , login: false
  , admin: false
  , authoring: false
  , accountManager: false
  , support: false
}

export default YTRoute;
